import React, { useState } from "react";
import api from "../api/http"; // <-- axios instance

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [isSending, setIsSending] = useState(false);
  const [serverMsg, setServerMsg] = useState(null);      // { type: 'success'|'error', text: string }
  const [fieldErrors, setFieldErrors] = useState({});     // backend 422 validation

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSending(true);
    setServerMsg(null);
    setFieldErrors({});

    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        message: form.message.trim(),
      };

      const { data } = await api.post("/contact", payload);
      if (data?.ok) {
        setServerMsg({ type: "success", text: data.message || "Message sent. We’ll get back to you soon." });
        setForm({ name: "", email: "", phone: "", message: "" });
      } else {
        setServerMsg({ type: "error", text: data?.message || "Something went wrong. Please try again." });
      }
    } catch (err) {
      // Handle 422 validation or network errors
      if (err?.response?.status === 422 && err.response.data?.errors) {
        setFieldErrors(err.response.data.errors);
        setServerMsg({ type: "error", text: "Please fix the highlighted fields." });
      } else {
        setServerMsg({ type: "error", text: "Unable to send right now. Please try again." });
      }
    } finally {
      setIsSending(false);
    }
  };

  return (
    <section id="contact" className="relative px-6 md:px-16 py-20">
      {/* Background image + overlay */}
      <div className="absolute inset-0">
        <img
          src="/images/getintouch.jpg"
          alt="Contact background"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-900/50" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-3xl mx-auto">
        <div className="relative rounded-2xl bg-white shadow-xl ring-1 ring-gray-100 p-6 md:p-8">
          <span className="absolute top-0 left-0 h-1.5 w-full bg-gradient-to-r from-blue-900 via-blue-300 to-blue-600 rounded-t-2xl" />

          <h2 className="text-3xl font-bold text-gray-900">Entrer en contact</h2>
          <p className="text-gray-500 mt-1">Nous répondons généralement dans les 24 heures.</p>

          {serverMsg && (
            <div
              className={`mt-4 rounded-md px-4 py-3 text-sm ${
                serverMsg.type === "success"
                  ? "bg-green-50 text-green-700 ring-1 ring-green-200"
                  : "bg-rose-50 text-rose-700 ring-1 ring-rose-200"
              }`}
            >
              {serverMsg.text}
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-8 space-y-6">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-semibold text-gray-900">
                Nom
              </label>
              <input
                id="name"
                name="name"
                type="text"
                placeholder="Votre Nom"
                value={form.name}
                onChange={handleChange}
                required
                className={`mt-2 w-full h-12 rounded-md border px-4 
                text-gray-900 placeholder-gray-400 
                bg-white focus:bg-white focus:text-gray-900
                focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 
                appearance-none dark:appearance-none ${
                  fieldErrors.name ? "border-rose-400" : "border-gray-200"
                }`}
              />
              {fieldErrors.name && <p className="mt-1 text-xs text-rose-600">{fieldErrors.name[0]}</p>}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-900">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                placeholder="Votre Email"
                value={form.email}
                onChange={handleChange}
                required
                className={`mt-2 w-full h-12 rounded-md border px-4 
                text-gray-900 placeholder-gray-400 
                bg-white focus:bg-white focus:text-gray-900
                focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 
                appearance-none dark:appearance-none ${
                  fieldErrors.email ? "border-rose-400" : "border-gray-200"
                }`}
              />
              {fieldErrors.email && <p className="mt-1 text-xs text-rose-600">{fieldErrors.email[0]}</p>}
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-gray-900">
                Téléphone
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                placeholder="Votre Numéro de Téléphone (Facultatif)"
                value={form.phone}
                onChange={handleChange}
                className={`mt-2 w-full h-12 rounded-md border px-4 
                text-gray-900 placeholder-gray-400 
                bg-white focus:bg-white focus:text-gray-900
                focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 
                appearance-none dark:appearance-none ${
                  fieldErrors.phone ? "border-rose-400" : "border-gray-200"
                }`}
              />
              {fieldErrors.phone && <p className="mt-1 text-xs text-rose-600">{fieldErrors.phone[0]}</p>}
            </div>

            {/* Message */}
            <div>
              <label htmlFor="message" className="block text-sm font-semibold text-gray-900">
                Message
              </label>
              <textarea
                id="message"
                name="message"
                rows={6}
                placeholder="Votre Message"
                value={form.message}
                onChange={handleChange}
                required
                className={`mt-2 w-full rounded-md border px-4 py-3 
                text-gray-900 placeholder-gray-400 
                bg-white focus:bg-white focus:text-gray-900
                focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 
                appearance-none dark:appearance-none ${
                  fieldErrors.message ? "border-rose-400" : "border-gray-200"
                }`}
              />
              {fieldErrors.message && <p className="mt-1 text-xs text-rose-600">{fieldErrors.message[0]}</p>}
            </div>

            {/* Submit */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isSending}
                className={`inline-flex items-center gap-2 rounded-md px-6 py-3 font-semibold text-white shadow 
                focus:outline-none focus:ring-2 focus:ring-cyan-500 
                ${isSending ? "bg-blue-800/70 cursor-not-allowed" : "bg-blue-900 hover:bg-blue-800"}`}
              >
                {isSending ? "Envoi..." : "Envoyer un message"}
                {!isSending && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
